import React, { CSSProperties, FunctionComponent, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ButtonWidget,
  CategoriesList,
  HeroBanner,
  HeroBannerCarousel,
  ImageAndTextbox,
  ImageWidget,
  InPageAnchor,
  PopularCategories,
  PreviouslyViewedProducts,
  ProductWidget,
  TextWidget,
  Video,
} from '..';
import MyAccountWrapper from '../../../../../components/user/my-account/my-account-wrapper-component';
import { IContentLayout } from '../../../models/campaign-pages/campaign-page.model';
import { ISides } from '../../../models/campaign-pages/common.model';
import { MetaDataAndTagging } from '../../shared';
import { CampaignPageProps, LayoutsRow, StructuredLayout } from './campaign-page-component.model';
import styles from './campaign-page-component.module.scss';

export const CampaignPage: FunctionComponent<CampaignPageProps> = (props: CampaignPageProps) => {
  let layouts = splitLayout();

  const scrolledRef = React.useRef(false);
  const { hash } = useLocation();

  // https://ericdcobb.medium.com/scrolling-to-an-anchor-in-react-when-your-elements-are-rendered-asynchronously-8c64f77b5f34
  useEffect(() => {
    //to ensure that the component has been full rendered
    setTimeout(() => {
      if (hash && !scrolledRef.current) {
        const id = hash.replace('#', '');

        const element = document.getElementById(id);

        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          scrolledRef.current = true;
        }
      }
    }, 100);
  }, [hash]);

  //set background color
  useEffect(() => {
    const pageContentWrapperElt = document.getElementById('pageWrapper');

    if (props.data.backgroundColour && pageContentWrapperElt) {
      if (!props.data.backgroundColour?.startsWith('#')) {
        pageContentWrapperElt?.classList.add(props.data.backgroundColour);
      }

      pageContentWrapperElt.style.backgroundColor = props.data.backgroundColour;
    }

    //clean up background color class and style
    return () => {
      if (props.data.backgroundColour && pageContentWrapperElt) {
        pageContentWrapperElt.classList.remove(props.data.backgroundColour);
        pageContentWrapperElt.style.backgroundColor = 'transparent';
      }
    };
  }, [props.data.backgroundColour]);

  return (
    <div
      className={`${styles['campaign-container']} ${
        !props.data.backgroundColour || props.data.backgroundColour?.startsWith('#') ? '' : styles[props.data.backgroundColour ?? '']
      }`}
      style={{ backgroundColor: props.data.backgroundColour ?? undefined } as CSSProperties}>
      {/* Head data */}
      <MetaDataAndTagging metaData={props.data.metaData} tagging={props.data.tagging} />

      {layouts.map((outerLayout) => {
        if (Array.isArray(outerLayout.layout)) {
          return getContentLayout(outerLayout.layout);
        } else
          return (
            //section
            <div
              className={`${styles['campaign-content-container']} ${
                !outerLayout.layout.widthType || outerLayout.layout.widthType === 'fixedWidth' ? `inner-page-wrapper ${styles.fixed}` : ''
              } ${outerLayout.layout.widthType === 'fullWidth' ? styles.full : ''}  ${
                !outerLayout.layout.backgroundColour || outerLayout.layout.backgroundColour?.startsWith('#')
                  ? ''
                  : styles[outerLayout.layout.backgroundColour ?? '']
              }`}
              style={
                {
                  '--border-top-left': outerLayout.layout.topBorderStyle === 'angledLeft' ? '4rem' : 0,
                  '--border-top-right': outerLayout.layout.topBorderStyle === 'angledRight' ? '4rem' : 0,
                  '--border-bottom-left': outerLayout.layout.bottomBorderStyle === 'angledLeft' ? 'calc(100% - 4rem)' : '100%',
                  '--border-bottom-right': outerLayout.layout.bottomBorderStyle === 'angledRight' ? 'calc(100% - 4rem)' : '100%',

                  '--full-width-padding-top':
                    outerLayout.layout.topBorderStyle === 'angledLeft' || outerLayout.layout.topBorderStyle === 'angledRight' ? '4rem' : 0,
                  '--full-width-padding-bottom':
                    outerLayout.layout.bottomBorderStyle === 'angledLeft' || outerLayout.layout.bottomBorderStyle === 'angledRight'
                      ? '4rem'
                      : 0,

                  '--desktop-outer-layout-margin': getSides(outerLayout.layout.margin?.desktop),
                  '--mobile-outer-layout-margin': getSides(outerLayout.layout.margin?.mobile),

                  '--desktop-outer-layout-padding': getSides(outerLayout.layout.padding?.desktop),
                  '--mobile-outer-layout-padding': getSides(outerLayout.layout.padding?.mobile),

                  '--outer-layout-background-colour': outerLayout.layout.backgroundColour ?? undefined,

                  backgroundImage: outerLayout.layout.backgroundImage ? `url("${outerLayout.layout.backgroundImage}")` : 'none',
                } as CSSProperties
              }>
              {getContentLayout(outerLayout.layout.contentLayouts, outerLayout.layout.widthType, outerLayout.layout.backgroundColour)}
            </div>
          );
      })}
    </div>
  );

  // split layouts => all layouts outside of a section until the first section
  function splitLayout() {
    let structuredLayout: StructuredLayout[] = [];
    let currContentArray: IContentLayout[] = [];

    for (let i = 0; i < props.data.layouts.length; i++) {
      let currLayout = props.data.layouts[i];

      if (currLayout.__typename === 'SectionLayout') {
        // if we are switching from section to content, push current array & reinit
        if (currContentArray.length) {
          structuredLayout.push({ layout: currContentArray });
          currContentArray = [] as IContentLayout[];
        }
        //push section
        structuredLayout.push({ layout: currLayout });
      } else if (currLayout.__typename === 'ContentLayout') {
        currContentArray.push(currLayout);
      }
    }

    //push anything left in the array
    if (currContentArray.length) {
      structuredLayout.push({ layout: currContentArray });
    }

    return structuredLayout;
  }

  function getContentLayout(
    contentLayout: IContentLayout[],
    outerLayoutWidthType?: string | null,
    outerLayoutBackgroundColour?: string | null
  ) {
    let layoutsInDesktopRow: LayoutsRow[] = getNumOfLayoutsInRow(
      contentLayout.map((l) => l.desktopWidth ?? '100'),
      contentLayout.length
    );

    let layoutsInTabletRow: LayoutsRow[] = getNumOfLayoutsInRow(
      contentLayout.map((l) => l.tabletWidth ?? '100'),
      contentLayout.length
    );

    let layoutsInMobileRow: LayoutsRow[] = getNumOfLayoutsInRow(
      contentLayout.map((l) => l.mobileWidth ?? '100'),
      contentLayout.length
    );

    return (
      <div
        className={`inner-page-wrapper ${styles['campaign-content']} ${
          outerLayoutWidthType === 'fixedWidth' ? styles['outer-fixed'] : ''
        } ${
          !outerLayoutBackgroundColour || outerLayoutBackgroundColour?.startsWith('#') ? '' : styles[outerLayoutBackgroundColour ?? '']
        }`}>
        {contentLayout
          //combine number of layouts in row with the layout
          .map((layout, i) => {
            return {
              numVisibleInDesktopRow: layoutsInDesktopRow[i].visibleLayoutsInRow,
              numOfLayoutsNotInLastDesktopRow: contentLayout.length - layoutsInDesktopRow[contentLayout.length - 1].layoutsInRow,
              indexOfLastLayoutInDesktopRow: layoutsInDesktopRow[i].indexOfLastLayoutInRow,

              numVisibleInTabletRow: layoutsInTabletRow[i].visibleLayoutsInRow,
              numOfLayoutsNotInLastTabletRow: contentLayout.length - layoutsInTabletRow[contentLayout.length - 1].layoutsInRow,
              indexOfLastLayoutInTabletRow: layoutsInTabletRow[i].indexOfLastLayoutInRow,

              numVisibleInMobileRow: layoutsInMobileRow[i].visibleLayoutsInRow,
              numOfLayoutsNotInLastMobileRow: contentLayout.length - layoutsInMobileRow[contentLayout.length - 1].layoutsInRow,
              indexOfLastLayoutInMobileRow: layoutsInMobileRow[i].indexOfLastLayoutInRow,

              layout: layout,
            };
          })
          .map((l, i) => {
            return (
              <div
                className={`${styles.layout} ${l.layout.enableBorderRadius ? styles.radius : ''} ${
                  l.layout.enableShadowOnHover ? styles.shadow : ''
                } ${
                  !l.layout.backgroundColour || l.layout.backgroundColour.startsWith('#') ? '' : styles[l.layout.backgroundColour ?? '']
                } ${!l.layout.borderColour || l.layout.borderColour.startsWith('#') ? '' : styles[`border-${l.layout.borderColour}`]}`}
                style={
                  {
                    '--desktop-num-layouts-in-row': l.numVisibleInDesktopRow,
                    '--tablet-num-layouts-in-row': l.numVisibleInTabletRow,
                    '--mobile-num-layouts-in-row': l.numVisibleInMobileRow,

                    //check if layout is in last row
                    '--desktop-margin-bottom': i >= l.numOfLayoutsNotInLastDesktopRow ? 0 : '1.6rem',
                    '--tablet-margin-bottom': i >= l.numOfLayoutsNotInLastTabletRow ? 0 : '1.6rem',
                    '--mobile-margin-bottom': i >= l.numOfLayoutsNotInLastMobileRow ? 0 : '0.8rem',

                    //check if layout is last layout in a row
                    '--desktop-margin-right':
                      i === l.indexOfLastLayoutInDesktopRow ||
                      contentLayout[i].desktopWidth === '0' ||
                      (i === l.indexOfLastLayoutInDesktopRow - 1 && contentLayout[i + 1].desktopWidth === '0') //for when hidden layout is the last layout
                        ? 0
                        : '1.6rem',
                    '--tablet-margin-right':
                      i === l.indexOfLastLayoutInTabletRow ||
                      contentLayout[i].tabletWidth === '0' ||
                      (i === l.indexOfLastLayoutInTabletRow - 1 && contentLayout[i + 1].tabletWidth === '0')
                        ? 0
                        : '1.6rem',
                    '--mobile-margin-right':
                      i === l.indexOfLastLayoutInMobileRow ||
                      contentLayout[i].mobileWidth === '0' ||
                      (i === l.indexOfLastLayoutInMobileRow - 1 && contentLayout[i + 1].mobileWidth === '0')
                        ? 0
                        : '0.8rem',

                    '--desktop-display': contentLayout[i].desktopWidth === '0' ? 'none' : 'flex',
                    '--tablet-display': contentLayout[i].tabletWidth === '0' ? 'none' : 'flex',
                    '--mobile-display': contentLayout[i].mobileWidth === '0' ? 'none' : 'flex',

                    '--desktop-width': `${l.layout.desktopWidth ?? 100}%`,
                    '--tablet-width': `${l.layout.tabletWidth ?? 100}%`,
                    '--mobile-width': `${l.layout.mobileWidth ?? 100}%`,

                    '--desktop-layout-padding': getSides(l.layout.padding?.desktop),
                    '--mobile-layout-padding': getSides(l.layout.padding?.mobile),

                    '--desktop-content-alignment': getContentLayoutAlignment(l.layout.desktopContentAlignment),
                    '--tablet-content-alignment': getContentLayoutAlignment(l.layout.tabletContentAlignment),
                    '--mobile-content-alignment': getContentLayoutAlignment(l.layout.mobileContentAlignment),

                    borderColor: l.layout.borderColour ?? undefined,
                    borderWidth: getSides(l.layout.borderSize),

                    backgroundColor: l.layout.backgroundColour ?? undefined,
                    backgroundImage: l.layout.backgroundImageUrl ? `url("${l.layout.backgroundImageUrl}")` : 'none',
                  } as CSSProperties
                }>
                {l.layout.widgets.map((w) => {
                  switch (w.__typename) {
                    case 'HeroBannerWidget':
                      if (w.banners.length === 1) return <HeroBanner banner={w.banners[0]}></HeroBanner>;
                      else if (w.banners.length > 1) return <HeroBannerCarousel widget={w}></HeroBannerCarousel>;
                      break;
                    case 'ImageTextBoxWidget':
                      return <ImageAndTextbox imageTextBox={w} isContainerFullHeight={l.layout.widgets.length === 1} />;
                    case 'PopularCategoriesWidget':
                      return <PopularCategories popularCategories={w} />;
                    case 'AllCategoriesWidget':
                      return <CategoriesList categoriesList={w} />;
                    case 'VideoDataWidget':
                      return <Video video={w} isContainerFullHeight={l.layout.widgets.length === 1} />;
                    case 'TextDataWidget':
                      return <TextWidget textWidget={w} isContainerFullHeight={l.layout.widgets.length === 1} />;
                    case 'ImageDataWidget':
                      return <ImageWidget image={w} isContainerFullHeight={l.layout.widgets.length === 1} />;
                    case 'ButtonDataWidget':
                      return <ButtonWidget button={w} isContainerFullHeight={l.layout.widgets.length === 1} />;
                    case 'PreviouslyViewedProducts':
                      return <PreviouslyViewedProducts />;
                    case 'InPageAnchorWidget':
                      return <InPageAnchor inPageAnchor={w} />;
                    case 'ProductDataWidget':
                      return <ProductWidget product={w} isContainerFullHeight={l.layout.widgets.length === 1} />;
                    case 'MyAccountAreaWidget':
                      return <MyAccountWrapper contextInfo={props.contextInfo} isInLoggedInHomepage={true} />;
                  }
                  return <></>;
                })}
              </div>
            );
          })}
      </div>
    );
  }

  function getSides(sides?: ISides | null): string {
    return sides ? `${sides.top ?? 0}px ${sides.right ?? 0}px ${sides.bottom ?? 0}px ${sides.left ?? 0}px` : '';
  }

  function getNumOfLayoutsInRow(breakpointWidths: string[], numOfLayouts: number): LayoutsRow[] {
    let width = 0,
      num = 0,
      displayedNum = 0,
      startOfRow = 0,
      numOfLayoutsInRows: LayoutsRow[] = [];

    for (let x = 0; x < numOfLayouts; x++) {
      const currWidth = parseInt(breakpointWidths[x]);

      //check if total width of layouts exceed 100% of container width
      if (width + currWidth <= 100) {
        //add layout to row
        width += currWidth;

        num++;
        if (currWidth > 0) displayedNum++; // only increment if visible
      } else {
        numOfLayoutsInRows = setNumOfLayoutsInRow(startOfRow, num, displayedNum, numOfLayoutsInRows, x - 1);

        //decrement the index of layouts since the layout at this index did not fit in the row
        //and thus was not considered
        x--;

        //resetting values to start new row
        num = displayedNum = width = 0;

        //set index of layout at which the next row starts
        startOfRow = x + 1;
      }
    }

    //if there are layouts on the last row which have not yet been considered
    if (width !== 0) {
      numOfLayoutsInRows = setNumOfLayoutsInRow(startOfRow, num, displayedNum, numOfLayoutsInRows, numOfLayouts - 1);
    }
    return numOfLayoutsInRows;
  }

  //sets number of layouts in a row and the index of the last layout in that row
  function setNumOfLayoutsInRow(
    startOfRow: number,
    num: number,
    visibleNum: number,
    layoutsInRow: LayoutsRow[],
    indexOfLastLayoutInRow: number
  ): LayoutsRow[] {
    for (let y = startOfRow; y < startOfRow + num; y++) {
      layoutsInRow[y] = { layoutsInRow: num, visibleLayoutsInRow: visibleNum, indexOfLastLayoutInRow: indexOfLastLayoutInRow };
    }

    return layoutsInRow;
  }

  function getContentLayoutAlignment(alignment?: string | null) {
    if (alignment !== null && alignment) {
      if (alignment === 'top') {
        return 'flex-start';
      } else if (alignment === 'bottom') {
        return 'flex-end';
      } else {
        return 'center';
      }
    } else {
      return 'top'; //default
    }
  }
};
